import React, { useState } from "react";
import styles from "./DiagnosisSuggestions.module.css";
import { useTranslation } from "react-i18next";

interface IIconProps {
  className: string;
}

interface IDiagnosisSuggestionsProps {
  data: {
    status: "ok" | "insufficient_data";
    status_note: string | null;
    alerts: {
      show: boolean;
      message: string; // "" when show=false
    };
    diagnoses: {
      name: string;
      probability_percent: number; // 0–100
      probability_label: "veryLikely" | "lessLikely" | "unlikely";
      reasoning: string;
    }[];
    suggested_tests: {
      name: string;
      modality: string;
      purpose: string;
      preparation: string | null;
      priority: "high" | "medium" | "low";
      invasiveness: "nonInvasive" | "minimallyInvasive" | "invasive";
      cost_tier: "low" | "medium" | "high";
    }[];
    insufficient_reasons: string[];
  },
  onFeedback?: (score: boolean, message: string) => void;
}

const IconChevronRight = (props : IIconProps) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
);
const IconChevronDown = (props : IIconProps) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
);
const IconAlert = (props : IIconProps) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);
const IconFlask = (props : IIconProps) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2h12"/>
    <path d="M14 2v6l7 9a2 2 0 0 1-1.6 3H4.6A2 2 0 0 1 3 17l7-9V2"/>
    <path d="M8.5 2h7"/>
  </svg>
);

function ConfidenceBar({ percentage = 0, label = "" }) {
  const width = Math.min(Math.max(percentage, 0), 100);
  return (
    <div className={styles.confidenceBar}>
      <p className={styles.confidenceLabel}>{label}</p>
      <div className={styles.confidenceBarBackground}>
        <div className={styles.confidenceBarFill} style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

export const DiagnosisSuggestions = (props: IDiagnosisSuggestionsProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<any>({});

  if (!props.data) return <div className={styles.noData}>{t('diagnosisSuggestions.noDataProvided')}</div>;

  if (props.data.status === "insufficient_data") {
    return (
      <div className={styles.container}>
        <h1 className={styles.title}>{t('diagnosisSuggestions.title')}</h1>
        <div className={styles.alertContainer}>
          <div className={styles.alert}>
            <div className={styles.alertContent}>
              <div>
                <h2 className={styles.alertTitle}>
                  <IconAlert className={styles.alertIcon} />
                  {t('diagnosisSuggestions.insufficientData')}
                </h2>
                {Array.isArray(props.data.insufficient_reasons) && props.data.insufficient_reasons.length > 0 ? (
                  <ul className={styles.alertList}>
                    {props.data.insufficient_reasons.map((r: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined, i: React.Key | null | undefined) => <li key={i}>{r}</li>)}
                  </ul>
                ) : <p className={styles.alertText}>{props.data.status_note}</p>}
              </div>
            </div>
          </div>
        </div>
        <p className={styles.missingKeyData}>{t('diagnosisSuggestions.missingKeyData')}</p>
      </div>
    );
  }

  const diagnoses = Array.isArray(props.data.diagnoses) ? props.data.diagnoses : [];
  const tests = Array.isArray(props.data.suggested_tests) ? props.data.suggested_tests : [];

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>{t('diagnosisSuggestions.title')}</h1>

      {props.data?.alerts?.show && props.data?.alerts?.message && (
        <div className={styles.alertContainer}>
          <div className={styles.alert}>
            <div className={styles.alertContent}>
              <h2 className={styles.alertTitle}>
                <IconAlert className={styles.alertIcon} />
                {t('diagnosisSuggestions.urgencyAlerts')}
              </h2>
              <p className={styles.alertText}>{props.data.alerts.message}</p>
            </div>
          </div>
        </div>
      )}

      <div className={styles.contentContainer}>
        {diagnoses.map((dx, i: React.Key | null | undefined) => {
          const isOpen = !!open[i as number];
          return (
            <div key={i} className={styles.diagnosisCard}>
              <button
                onClick={() => setOpen((o: any[]) => ({ ...o, [i as number]: !o[i as number] }))}
                className={styles.diagnosisButton}
              >
                <span>{dx.name}</span>
                {isOpen ? <IconChevronDown className={styles.chevronIcon} /> : <IconChevronRight className={styles.chevronIcon} />}
              </button>

              <ConfidenceBar percentage={dx.probability_percent ?? 0} label={t(`diagnosisSuggestions.${dx.probability_label}`) ?? ""} />

              {isOpen && (
                <div className={styles.reasoningContent}>
                  <p className={styles.reasoningText}>{dx.reasoning}</p>
                  {/* <div className={styles.reportButtonContainer}>
                    <button className={styles.reportButton}>
                      {t('diagnosisSuggestions.getFullReport')}
                      <svg className={styles.reportIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M5 21q-.825 0-1.412-.587T3 19V5q0-.825.588-1.412T5 3h11l5 5v11q0 .825-.587 1.413T19 21zm0-2h14V9h-4V5H5zm2-2h10v-2H7zm0-8h5V7H7zm0 4h10v-2H7zM5 5v4zv14z"/>
                      </svg>
                    </button>
                  </div> */}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className={styles.testsSection}>
        <div className={styles.testsHeader}>
          <IconFlask className={styles.testsIcon} />
          <h2 className={styles.testsTitle}>{t('diagnosisSuggestions.suggestedTests')}</h2>
        </div>
        <p className={styles.testsDescription}>{t('diagnosisSuggestions.testsDescription')}</p>
        <ul className={styles.testsList}>
          {tests.map((test: { name: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined; purpose: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined; preparation: any; priority: any; invasiveness: any; cost_tier: any; }, i: React.Key | null | undefined) => (
            <li key={i}>
              <span className={styles.testName}>{test.name}</span>: {test.purpose}<br />
              <span className={styles.testDetails}>
                {t('diagnosisSuggestions.preparation')}: {test.preparation || "—"} | {t('diagnosisSuggestions.priority')}: {t(`common.${test.priority}`) || "—"} | {t('diagnosisSuggestions.invasiveness')}: {t(`diagnosisSuggestions.${test.invasiveness}`) || "—"} | {t('diagnosisSuggestions.cost')}: {t(`common.${test.cost_tier}`) || "—"}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
